import { Link, Typography } from '@mui/material';
import {
	StyledEngineProvider,
	Theme,
	ThemeProvider,
} from '@mui/material/styles';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import delve from 'dlv';
import React from 'react';
import { FeeConfigurationResponse } from '../../../api/ConfigApi';
import PolicyApi from '../../../api/PolicyApi';
import { AuthData } from '../../../context/auth';
import { PolicyData, withPolicyContext } from '../../../context/policy';
import { CurrencyCode } from '../../../types/currencyCode';
import { Fee, FeeUpdateRequest } from '../../../types/policy';
import { tableStyles } from '../../../util/styles';
import { HeadCell, Order, sortTableByKey } from '../../../util/tableUtil';
import theme from '../../../util/theme';
import { capitalize, formatCurrency } from '../../../util/util';
import {
	StyledEmptyMessage,
	StyledTitle,
	StyledTitleContainer,
} from '../../common';
import DefaultDialog from '../../DefaultDialog';
import SortableTable from '../../SortableTable';
import { errorToast, successToast } from '../../Toast';
import AddFeeModal from './AddFeeModal';

declare module '@mui/styles/defaultTheme' {
	interface DefaultTheme extends Theme {}
}

interface Props {
	policyData: PolicyData;
	authData: AuthData;
}

const getColumns = (isInEditMode: boolean) => {
	const columns = [
		{ id: 'name', rightAlign: false, label: 'Name' },
		{
			id: 'amount',
			rightAlign: true,
			label: 'Amount',
			className: 'feesAmountHeader',
		},
		{ id: 'startTimestamp', rightAlign: false, label: 'Date' },
		{ id: 'description', rightAlign: false, label: 'Description' },
		{ id: 'status', rightAlign: false, label: 'Status' },
	] as HeadCell[];

	if (isInEditMode) {
		columns.unshift({ id: 'reversed', rightAlign: false, label: 'Actions' });
	}
	return columns;
};

interface ExtendedFee extends Fee {
	displayName: string;
	status: string;
}

interface ReverseFeeModalProps {
	fee: ExtendedFee;
	authData: AuthData;
	policyLocator: string;
	isInEditMode: boolean;
	updateFeesTable(): void;
	currency: CurrencyCode;
}

const FeeRow = ({
	fee,
	authData,
	policyLocator,
	isInEditMode,
	updateFeesTable,
	currency,
}: ReverseFeeModalProps) => {
	const [open, setOpen] = React.useState(false);
	const [submitting, setSubmitting] = React.useState(false);
	const closeModal = () => setOpen(false);
	const openModal = () => setOpen(true);
	const classes = tableStyles();

	const reverseFee = async (feeToUpdate: Fee) => {
		setSubmitting(true);
		const request: FeeUpdateRequest = {
			reverseFees: [feeToUpdate.locator],
		};
		try {
			const response = await PolicyApi.addFee(
				authData.auth.authorizationToken,
				policyLocator,
				request,
			);
			if (response.status === 200) {
				closeModal();
				updateFeesTable();
				successToast(
					`Successfully reversed fee "${capitalize(
						fee.name,
					)}" of amount ${formatCurrency(parseFloat(fee.amount), currency)}`,
				);
			}
		} catch (e) {
			errorToast(e.message);
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<>
			<DefaultDialog
				actions={[
					{
						text: 'Reverse',
						onClick: () => reverseFee(fee),
						disabled: submitting,
					},
					{ text: 'Close', onClick: closeModal, disabled: submitting },
				]}
				open={open}
				onClose={close}
				title='Reverse Fee'
			>
				<Typography>Are you sure you want to reverse this fee?</Typography>
			</DefaultDialog>
			<TableRow hover tabIndex={-1} className={classes.tableRow}>
				{isInEditMode && (
					<TableCell>
						<Link
							className={fee.reversed ? classes.disabledLink : ''}
							underline={fee.reversed ? 'none' : 'always'}
							onClick={!fee.reversed ? openModal : undefined}
						>
							Reverse
						</Link>
					</TableCell>
				)}
				<TableCell>{fee.displayName}</TableCell>
				<TableCell align='right'>
					{formatCurrency(parseFloat(fee.amount), currency)}
				</TableCell>
				<TableCell>{authData.getFormattedDate(fee.startTimestamp)}</TableCell>
				<TableCell>{fee.description}</TableCell>
				<TableCell>{fee.status}</TableCell>
			</TableRow>
		</>
	);
};

const Fees = ({
	authData,
	policyData: { policy, productConfig, loadData },
}: Props) => {
	const [fees, setFees] = React.useState<Fee[]>(delve(policy, 'fees', []));
	const [loading, setLoading] = React.useState(false);
	const { effectiveContractEndTimestamp } = policy;
	const feeOptions: FeeConfigurationResponse[] = delve(
		productConfig,
		'policyConfiguration.fees',
		[],
	);
	const currency = policy?.currency;
	const { policyStartTimestamp } = policy.characteristics[0];
	const [order, setOrder] = React.useState<Order>('desc');
	const [orderBy, setOrderBy] = React.useState<keyof Fee>('startTimestamp');
	const isInEditMode = authData.hasPermission('policy.fee');
	const emptyTable = !fees.length ? (
		<StyledEmptyMessage>No fees</StyledEmptyMessage>
	) : null;

	const handleSetOrder = (key: string) => {
		setOrderBy(key as keyof Fee);
	};

	const updateFeesTable = async () => {
		setLoading(true);
		try {
			const response = await loadData(policy.locator);
			setFees(response.policy.fees);
		} catch (e) {
			errorToast(e.message);
		} finally {
			setLoading(false);
		}
	};

	// Status attribute and displayName required for sorting since it does not belong in Fee type
	const feesWithStatus: Fee[] = fees.map((fee) => ({
		...fee,
		status: fee.reversed ? 'Reversed' : 'Active',
		displayName: feeOptions.find((f) => f.name === fee.name)?.displayName,
	}));
	const rowElements = sortTableByKey(
		feesWithStatus,
		orderBy,
		order,
		orderBy === 'amount' ? 'number' : 'string',
	).map((row: ExtendedFee, index: number) => (
		<FeeRow
			fee={row}
			key={index}
			isInEditMode={isInEditMode}
			authData={authData}
			policyLocator={policy.locator}
			updateFeesTable={updateFeesTable}
			currency={currency}
		/>
	));

	return (
		<StyledEngineProvider injectFirst>
			<ThemeProvider theme={theme}>
				<StyledTitleContainer>
					<StyledTitle>Fees</StyledTitle>
					{isInEditMode && feeOptions.length > 0 && (
						<AddFeeModal
							{...{
								effectiveContractEndTimestamp,
								policyStartTimestamp,
								feeOptions,
								currency,
								authToken: authData.auth.authorizationToken,
								policyLocator: policy.locator,
								updateFeesTable,
							}}
						/>
					)}
				</StyledTitleContainer>
				<hr />
				<SortableTable
					loading={loading}
					rows={rowElements}
					headCells={getColumns(isInEditMode)}
					additionalMsg={emptyTable}
					order={order}
					setOrder={setOrder}
					orderBy={orderBy}
					setOrderBy={handleSetOrder}
				/>
			</ThemeProvider>
		</StyledEngineProvider>
	);
};

export default withPolicyContext<Props>(Fees);
